import * as XLSX from "xlsx";
import { Employee } from "../types/processTypes";
import { formatReferenceNumber } from "../utils/referenceNumber";
import { Button, Stack } from "@mui/material";
import { Upload } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { differenceInMonths, format } from "date-fns";

interface ExportProps {
  selectedEmployees: Employee[];
}

function ExportToExcel({ selectedEmployees }: ExportProps) {
  const { t: te } = useTranslation("enums");

  const handleFullDownload = () => {
    // flatten object like this {id: 1, title:'', category: ''};
    const rows = selectedEmployees.map((employee) => ({
      fullName: employee.givenName + " " + employee.surName,
      passportNumber: employee.passportNumber,
      sex: te(employee.sex as string),
      placeOfBirth: employee.placeOfBirth,
      dateOfBirth: format(
        new Date(employee.dateOfBirth as Date),
        "dd-MMM-yyyy",
      ).toUpperCase(),
      age: Math.floor(
        differenceInMonths(new Date(), new Date(employee.dateOfBirth as Date)) /
          12,
      ),
      passportIssueDate: format(
        new Date(employee.passportIssueDate as Date),
        "dd-MMM-yyyy",
      ).toUpperCase(),
      placeOfIssue: employee.placeOfIssue,
      passportExpiryDate: format(
        new Date(employee.passportExpiryDate as Date),
        "dd-MMM-yyyy",
      ).toUpperCase(),
      religion: te(employee.additionalData?.religion as string),
      maritalStatus: te(employee.additionalData?.maritalStatus as string),
      appliedProfession: te(
        employee.additionalData?.appliedProfession as string,
      ),
      telephone: employee.address?.telephone,
      contactName: employee.contactPerson?.displayName,
      contactTelephone: employee.contactPerson?.address?.telephone,
      arabic: te(employee.education?.arabicLanguage as string),
      english: te(employee.education?.englishLanguage as string),
      referenceNumber: formatReferenceNumber(employee.referenceNumber),

      visaNumber: employee.visa?.visaNumber,
      sponsorName: employee.visa?.fullName,
      sponsorId: employee.visa?.passportNumber,
      sponsorTelephone: employee.visa?.address?.telephone,
      sponsorCity: employee.visa?.address?.city,
      applicationNumber: employee.embassyProcess?.enjazNumber,
    }));
    // console.log(selectedEmployees);
    // create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);

    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 10 },
      { wch: 20 },
      { wch: 15 },
      { wch: 5 },
      { wch: 15 },
      { wch: 20 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 },
      { wch: 20 },
      { wch: 10 },
      { wch: 10 },
      { wch: 20 },
      { wch: 15 },
      { wch: 30 },
      { wch: 15 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    // customize header names
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        [
          "Full Name",
          "Passport No.",
          "Sex",
          "Place Of Birth",
          "Birth Date",
          "Age",
          "Issue Date",
          "Place Of Issue",
          "Expiry Date",
          "Religion",
          "Marital Status",
          "Profession",
          "Tel.",
          "Contact Name",
          "Contact Tel.",
          "Arabic",
          "English",
          "Reference No.",
          "Visa No.",
          "Sponsor Name",
          "Sponsor Id",
          "Sponsor Tel.",
          "Sponsor City",
          "Application No.",
        ],
      ],
      { origin: "A1" },
    );

    XLSX.writeFile(workbook, new Date().toLocaleDateString() + "_Export.xlsx", {
      compression: true,
    });
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Button
        color="secondary"
        variant="contained"
        onClick={handleFullDownload}
        size="medium"
      >
        <Upload /> Export to Excel
      </Button>
    </Stack>
  );
}

export default ExportToExcel;
